export const DAY_MS = 86_400_000;

export function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Whole days from today to the due day: negative when late, 0 today. */
export function dueDays(dueAt: number, now: number): number {
  return Math.round((startOfDay(dueAt) - startOfDay(now)) / DAY_MS);
}

/** A due date carries a time unless it sits at local midnight, which the date picker alone produces. */
export function hasDueTime(dueAt: number): boolean {
  const d = new Date(dueAt);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

/** The same day at the given "HH:MM", or at midnight (date only) when no time is given. */
export function withTime(dayTs: number, time: string | null): number {
  const d = new Date(dayTs);
  const m = time ? /^(\d{2}):(\d{2})/.exec(time) : null;
  d.setHours(m ? Number(m[1]) : 0, m ? Number(m[2]) : 0, 0, 0);
  return d.getTime();
}

export function toTimeString(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Late once the time has passed when there is one, otherwise once the day is over. */
export function isLate(dueAt: number, now: number): boolean {
  return hasDueTime(dueAt) ? now > dueAt : dueDays(dueAt, now) < 0;
}

/** hour12 undefined leaves the clock to the locale; h23 avoids a "24:05" that some locales produce. */
export function formatTime(ts: number, locale: string, hour12?: boolean): string {
  return new Date(ts).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', ...(hour12 === undefined ? {} : { hourCycle: hour12 ? 'h12' : 'h23' }) });
}

export function toISODate(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function fromISODate(s: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** Local wall time for a datetime-local input. */
export function toLocalDateTime(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function fromLocalDateTime(s: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export function formatShortDate(ts: number, locale: string): string {
  return new Date(ts).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
